import React, { createContext, useContext, useEffect, useState } from "react";
import { supabase } from "../lib/supabaseClient";

const AuthContext = createContext(null);

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within an AuthProvider");
  return ctx;
};

export const AuthProvider = ({ children }) => {
  const [authUser, setAuthUser] = useState(null); // Supabase auth user
  const [userType, setUserType] = useState(null); // "company" or "host"
  const [loading, setLoading] = useState(true);

  // Helper: read userType from Supabase user metadata
  const applyUser = (user) => {
  setAuthUser(user || null);

  const type =
    user?.user_metadata?.userType ||
    user?.user_metadata?.user_type ||
    "company";

  setUserType(type === "host" ? "host" : "company");
};

  useEffect(() => {
    // 1) Get initial session (refresh-safe)
    const init = async () => {
      const { data, error } = await supabase.auth.getSession();
      if (!error) applyUser(data?.session?.user || null);
      setLoading(false);
    };
    init();

    // 2) Listen for auth changes (login/logout/reset)
    const { data: sub } = supabase.auth.onAuthStateChange((_event, session) => {
      applyUser(session?.user || null);
      setLoading(false);
    });

    return () => {
      sub?.subscription?.unsubscribe();
    };
  }, []);

  const logout = async () => {
    await supabase.auth.signOut();
    // onAuthStateChange will update state
  };

  // ⚠️ Keep same shape your app expects:
  const value = {
    user: authUser
  ? {
      id: authUser.id,
      email: authUser.email,
      userType: userType,
      name: authUser.user_metadata?.name || "Account",
    }
  : null,
    authUser, // raw supabase user if you ever need it
    userType,
    loading,
    logout,
    isAuthenticated: !!authUser,
    isCompany: userType === "company",
    isHost: userType === "host",
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
